import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Chip, CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router';
import { useAlert, useUser } from '../../contexts';
import api, { ApiError, isApiError } from '../../api/api';
import ConnectorReferralModal from '../connector/ConnectorReferralModal';
import { ConnectorReferralOption, Role, connectorReferralOptionFromInvestorProfile, InvestorMatchOutput, MatchListType, FilterOption, ConnectorDeal, ConnectorDealStatus, PAGE_SIZE } from '../../types/types';
import { StyledText } from '../../components/StyledText';
import { MatchedInvestorPagePathState, MatchedInvestorPageProps, MatchedInvestorTabPathParams } from '../../types/router';
import MatchedInvestorTab, { TabState } from './MatchedInvestorTab';
import CheckingForUpdates from '../../components/CheckingForUpdates';
import { exporter } from '../../lib/utils';

const makeQueryFromPageFiltersList = (page: number, filters: FilterOption[], listType: MatchListType) => {
  const fromCountQuery = `from=${(page - 1) * PAGE_SIZE}&count=${PAGE_SIZE}`;
  const listTypeQuery = `&listType=${listType}`;
  const filterQuery = filters.length > 0 ? `&${filters.map((i) => i.query).join('&')}` : '';
  return `${fromCountQuery}${listTypeQuery}${filterQuery}`;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(1)
  },
  investorList: {
    '& > .MuiAccordion-root': {
      marginBottom: theme.spacing(2)
    }
  },
  summary: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  centerItems: {
    display: 'flex',
    alignItems: 'center'
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  filterChipContainer: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(1, 0)
  },
  statusChip: {
    marginRight: theme.spacing(1)
  },
  filterButton: {
    width: 100
  }
}));

type FilterMap = Record<MatchListType, FilterOption[]>;
type PageMap = Record<MatchListType, number>;

interface MatchedInvestorPagePathParams {
  profileId: string
}

const MatchedInvestorPage: React.FC<MatchedInvestorPageProps> = ({
  entProfile, entProfileLoading, dealInfo, setDealInfo
}) => {
  const styles = useStyles();
  const { postNewAlert } = useAlert();

  // Mark: - User States
  const { getAccessToken, userInfo } = useUser();
  const isConnector = useMemo(() => (!!(userInfo && userInfo.role === Role.Connector)), [userInfo]);

  const [loading, setLoading] = useState<string[]>([]);
  const [loaded, setLoadeded] = useState<Record<string, TabState>>({});
  const [needsUpdate, setNeedsUpdate] = useState<string|null>(null);

  // Mark: - React Router States
  const history = useHistory<MatchedInvestorPagePathState | undefined>();
  const { url } = useRouteMatch();
  const listRoute = useRouteMatch<MatchedInvestorTabPathParams>('/entrepreneur/:profileId/matched-investors/:listType');
  const pathParams = useParams<MatchedInvestorPagePathParams>();
  const pathState = history.location.state;
  const { profileId } = pathParams;
  const entProfileId = parseInt(profileId);
  const listType = useMemo<MatchListType>(() => {
    if (listRoute?.params?.listType) {
      return listRoute.params.listType;
    } else {
      return MatchListType.requested;
    }
  }, [listRoute]);

  type Metadata = { requested: number, recommended: number, sent: number };
  const [metadata, setMetadata] = useState<Metadata|null>(null);

  // MARK: - Referral Related States
  const [pendingReferral, setPendingReferral] = useState<ConnectorReferralOption>();

  const pageMap = useMemo<PageMap>(() => {
    const defaultPageMap: PageMap = { sent: 1, requested: 1, recommended: 1 };
    if (pathState?.pageMap) {
      return pathState.pageMap;
    } else {
      return defaultPageMap;
    }
  }, [pathState?.pageMap]);

  const filterMap = useMemo<FilterMap>(() => {
    const fm: FilterMap = { sent: [], requested: [], recommended: [] };
    if (pathState?.filterMap) {
      return pathState.filterMap;
    } else {
      return fm;
    }
  }, [pathState?.filterMap]);

  // eslint-disable-next-line arrow-body-style
  const currentFilters = useMemo(() => {
    return filterMap[listType] || [];
  }, [filterMap, listType]);

  // eslint-disable-next-line arrow-body-style
  const currentPage = useMemo(() => {
    return pageMap[listType];
  }, [pageMap, listType]);

  const currentQuery = useMemo(() => makeQueryFromPageFiltersList(currentPage, currentFilters, listType), [currentPage, currentFilters, listType]);

  const currrentItemCount = useMemo(() => (metadata ? metadata[listType] : 0), [metadata, listType]);

  // eslint-disable-next-line arrow-body-style
  const currentTabState: TabState = useMemo(() => {
    return loaded[`${currentQuery}`] || { listModel: undefined, error: undefined };
  }, [currentQuery, loaded]);

  useEffect(() => {
    if (currentTabState.listError) {
      postNewAlert('An unexpected error occurred while fetching your investor list. Please try again later.');
    }
  }, [currentTabState.listError, postNewAlert]);

  useEffect(() => {
    setNeedsUpdate(currentQuery);
  }, [currentQuery]);

  const updatePageMap = useCallback((
    args: { page: number, list: MatchListType }
  ) => {
    pageMap[args.list] = args.page;
    history.push(`${listRoute?.url}`, {
      ...pathState,
      pageMap: { ...pageMap }
    });
  }, [pageMap, history, listRoute?.url, pathState]);

  const updateInvestorList = useCallback(({ query, response, error }) => {
    const newLoadedState = { ...loaded };
    let newMetadata: Metadata | undefined;
    if (response !== undefined) {
      const newEntry:InvestorMatchOutput[] = response.data;
      newLoadedState[`${query}`] = { listModel: newEntry, listError: undefined };
      newMetadata = response.metadata;
    } else {
      newLoadedState[`${query}`] = { listError: error, listModel: undefined };
    }
    setLoadeded(newLoadedState);
    if (newMetadata !== undefined) {
      setMetadata(newMetadata);
    }
  }, [loaded]);

  useEffect(() => {
    // eslint-disable-next-line arrow-body-style
    const reduceFilterOptions = (options: FilterOption[]) => {
      return options.reduce((prev: any, current) => {
        if (current.key === 'minMatchScore' && typeof current.value === 'number') {
          prev[`${current.key}`] = current.value / 100.0;
        } else {
          prev[`${current.key}`] = current.value;
        }
        return prev;
      }, {});
    };
    const fetchTheData = async () => {
      // console.log('fetching data with current query: ', currentQuery);
      const query = `${currentQuery}`;
      setLoading((previousState) => [...previousState, query]);

      try {
        const recData = {
          listType: MatchListType.recommended,
          filters: reduceFilterOptions(filterMap.recommended || [])
        };
        const reqData = {
          listType: MatchListType.requested,
          filters: reduceFilterOptions(filterMap.requested || [])
        };
        const sentData = {
          listType: MatchListType.sent,
          filters: reduceFilterOptions(filterMap.sent || [])
        };
        const response = await api.getMatchedInvestorsForEnt(await getAccessToken(), {
          entId: entProfileId,
          query,
          body: { recommended: recData, requested: reqData, sent: sentData }
        });
        updateInvestorList({ query, response });
      } catch (error: unknown) {
        console.log('error fetching data: ', error);

        if (isApiError(error)) {
          updateInvestorList({ query, response: undefined, error: (error as ApiError).code });
        } else {
          updateInvestorList({ query, response: undefined, error: 'unable to fetch data' });
        }
      } finally {
        setLoading((previousState) => previousState.filter((q) => q !== query));
      }
    };

    const shouldFetch = (needsUpdate === currentQuery) || (loading.find((q) => q === currentQuery) === undefined && loaded[`${currentQuery}`] === undefined);
    if (shouldFetch) {
      if (needsUpdate === currentQuery) {
        setNeedsUpdate(null);
      }
      fetchTheData();
    }
  }, [currentQuery, entProfileId, getAccessToken, loaded, loading, needsUpdate, pageMap, filterMap, updateInvestorList]);

  const startReferral = useCallback((investor: InvestorMatchOutput) => {
    setPendingReferral(connectorReferralOptionFromInvestorProfile(investor));
  }, []);

  const handleDealStatusChange = useCallback((newStatus: ConnectorDealStatus) => {
    if (!dealInfo.deal) {
      return;
    }
    const newDeal: ConnectorDeal = { ...dealInfo.deal, status: newStatus };
    setDealInfo({ ...dealInfo, deal: newDeal });
  }, [dealInfo, setDealInfo]);

  const handleSendReferrals = useCallback(async (values: any) => {
    if (!pendingReferral) {
      return;
    }

    const investors = [{ email: pendingReferral.email, name: pendingReferral.contactName, id: pendingReferral.investorProfileId }];
    const { referralMessage, additionalEmails } = values;
    const input = { investors, entId: entProfile?.id, message: referralMessage, additionalRecipients: additionalEmails };

    const token = await getAccessToken();
    try {
      const { dealStatusUpdated } = await api.sendDealReferrals(token, input);
      setPendingReferral(undefined);
      if (dealStatusUpdated) {
        handleDealStatusChange(ConnectorDealStatus.InProgress);
      }
      setNeedsUpdate(currentQuery);
    } catch (e: unknown) {
      if (isApiError(e)) {
        const { message } = (e as ApiError);
        postNewAlert(message);
      }
    }
  }, [currentQuery, entProfile?.id, getAccessToken, handleDealStatusChange, pendingReferral, postNewAlert]);

  const handleListTypeChange = useCallback((list: MatchListType) => {
    history.push(`${url}/${list}`, {
      ...pathState
    });
  }, [history, pathState, url]);

  const updateFilterMap = useCallback((
    args: { list: MatchListType, filters: FilterOption[] }
  ) => {
    const filterMapCloned = { ...filterMap };
    const pageMapCloned = { ...pageMap };
    filterMapCloned[args.list] = args.filters;
    pageMapCloned[args.list] = 1;
    history.replace(`${listRoute?.url}`, {
      ...pathState,
      filterMap: filterMapCloned,
      pageMap: pageMapCloned
    });
  }, [filterMap, pageMap, history, listRoute?.url, pathState]);

  useEffect(() => {
    if (needsUpdate !== null) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [needsUpdate]);

  const refreshHandler = useCallback(() => {
    setNeedsUpdate(currentQuery);
  }, [currentQuery]);

  const downloadToCSV = useCallback(async (startFrom: number) => {
    // eslint-disable-next-line arrow-body-style
    const reduceFilterOptions = (options: FilterOption[]) => {
      return options.reduce((prev: any, current) => {
        if (current.key === 'minMatchScore' && typeof current.value === 'number') {
          prev[`${current.key}`] = current.value / 100.0;
        } else {
          prev[`${current.key}`] = current.value;
        }
        return prev;
      }, {});
    };
    const recData = {
      listType: MatchListType.recommended,
      filters: reduceFilterOptions(filterMap.recommended || [])
    };
    const reqData = {
      listType: MatchListType.requested,
      filters: reduceFilterOptions(filterMap.requested || [])
    };
    const sentData = {
      listType: MatchListType.sent,
      filters: reduceFilterOptions(filterMap.sent || [])
    };
    const query = `from=${startFrom}&count=${startFrom + 1000}&listType=${listType}`;
    const { data } = await api.getMatchedInvestorsForEnt(await getAccessToken(), {
      entId: entProfileId,
      query,
      body: { recommended: recData, requested: reqData, sent: sentData }
    });

    await exporter(data, ['id', 'intro', 'totalBase', 'profileImage', 'connectorStatusMap'], `Ent-${entProfileId}_DealStatus-${listType}`);
  }, [listType, entProfileId, filterMap.recommended, filterMap.requested, filterMap.sent, getAccessToken]);

  const pageIsLoading = useMemo(() => (loading.find((q) => q === currentQuery) !== undefined), [currentQuery, loading]);
  const pageHasData = useMemo(() => (currentTabState.listModel && currentTabState.listModel.length > 0), [currentTabState.listModel]);

  if (entProfileLoading) {
    return <CircularProgress />;
  }

  return (
    <Grid container className={styles.root}>
      {
        isConnector
        && (
        <ConnectorReferralModal
          open={pendingReferral != null}
          referralOptions={[]}
          onClose={() => { setPendingReferral(undefined); }}
          handleSubmit={handleSendReferrals}
          preselected={pendingReferral}
        />
        )
      }
      <Grid container item xs={12} style={{ marginBottom: 20 }}>
        <Grid item>
          <Chip
            style={{ marginRight: 15 }}
            label='Requested'
            clickable
            color={listType === MatchListType.requested ? 'primary' : 'default'}
            variant='outlined'
            deleteIcon={(
              <StyledText
                color='primary'
                align='center'
                style={{ fontSize: 13, fontWeight: 500, padding: '3px 0' }}
              >
                {metadata?.requested || 0}
              </StyledText>
              )}
            onDelete={() => handleListTypeChange(MatchListType.requested)}
            onClick={() => handleListTypeChange(MatchListType.requested)}
          />
        </Grid>
        <Grid item>
          <Chip
            style={{ marginRight: 15 }}
            label='Recommended'
            clickable
            color={listType === MatchListType.recommended ? 'primary' : 'default'}
            variant='outlined'
            deleteIcon={(
              <StyledText
                color='primary'
                align='center'
                style={{ fontSize: 13, fontWeight: 500, padding: '3px 0' }}
              >
                {metadata?.recommended || 0}
              </StyledText>
              )}
            onDelete={() => handleListTypeChange(MatchListType.recommended)}
            onClick={() => handleListTypeChange(MatchListType.recommended)}
          />
        </Grid>
        <Grid item>
          <Chip
            style={{ marginRight: 15 }}
            label={isConnector ? 'Sent' : 'Referred'}
            clickable
            color={listType === MatchListType.sent ? 'primary' : 'default'}
            variant='outlined'
            deleteIcon={(
              <StyledText
                color='primary'
                align='center'
                style={{ fontSize: 13, fontWeight: 500, padding: '3px 0' }}
              >
                {metadata?.sent || 0}
              </StyledText>
              )}
            onDelete={() => handleListTypeChange(MatchListType.sent)}
            onClick={() => handleListTypeChange(MatchListType.sent)}
          />
        </Grid>
        { (pageIsLoading && pageHasData) && (
          <Grid item>
            <CheckingForUpdates style={{ marginRight: 15, marginLeft: 5 }} />
          </Grid>
        ) }
      </Grid>
      <Switch>
        <Route
          path='/entrepreneur/:profileId/matched-investors/:listType'
          render={() => (
            <MatchedInvestorTab
              entProfile={entProfile}
              listFilter={currentFilters}
              listPage={currentPage}
              tabState={currentTabState}
              itemCount={currrentItemCount}
              pageIsLoading={loading.find((q) => q === currentQuery) !== undefined}
              startReferral={startReferral}
              updatePageMap={updatePageMap}
              updateFilterMap={updateFilterMap}
              refreshHandler={refreshHandler}
              handleExportToCsv={downloadToCSV}
            />
          )}
        />
      </Switch>
    </Grid>
  );
};

export default MatchedInvestorPage;

